import { useState, useEffect, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { createSelector } from 'reselect'
import swal from 'sweetalert'
import { Container, Button } from 'reactstrap'
import { FaEnvelope } from 'react-icons/fa'
import * as ACT from 'components/Contact/actions'
import { CONTACT_DEALS_STATUS } from 'components/Contact/constants'
import { EmailContactModalContainer } from 'components/common/modals/EmailContactModalContainer'
import s from './Deals.scss'
import { EMAIL_TEMPLATE_TYPE } from 'utils/constants'
import { Tabs } from 'components/ideal-core-ui'
import { DealsTable } from 'components/common/DealsTable'

const selectCompanyDeals = createSelector([state => state.companyDeals], deals => {
  return deals
})

const Deals = ({ deals, lostDeals, closedDeals, contact, projectDeals, wonDeals }) => {
  const [contactDeals, setContactDeals] = useState(deals)
  const [currentDealsStatus, setCurrentDealsStatus] = useState(CONTACT_DEALS_STATUS.ACTIVE)
  const [showEmailContactModal, setShowEmailContactModall] = useState(false)
  const [emailTemplateType, setEmailTemplateType] = useState('')

  const dispatch = useDispatch()

  const companyDeals = useSelector(state => selectCompanyDeals(state.contact))

  const filterDeals = useCallback(
    status => {
      switch (status) {
        case CONTACT_DEALS_STATUS.LOST:
          setContactDeals(lostDeals)
          setCurrentDealsStatus(CONTACT_DEALS_STATUS.LOST)
          break
        case CONTACT_DEALS_STATUS.PROJECT:
          setContactDeals(projectDeals)
          setCurrentDealsStatus(CONTACT_DEALS_STATUS.PROJECT)
          break
        case CONTACT_DEALS_STATUS.WON:
          setContactDeals(wonDeals)
          setCurrentDealsStatus(CONTACT_DEALS_STATUS.WON)
          break
        case CONTACT_DEALS_STATUS.CLOSED:
          setContactDeals(closedDeals)
          setCurrentDealsStatus(CONTACT_DEALS_STATUS.CLOSED)
          break
        default:
          setContactDeals(deals)
          setCurrentDealsStatus(CONTACT_DEALS_STATUS.ACTIVE)
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [closedDeals, deals, lostDeals, projectDeals, wonDeals]
  )

  useEffect(() => {
    filterDeals(currentDealsStatus)
  }, [currentDealsStatus, filterDeals])

  const toggleEmailContactModal = () => setShowEmailContactModall(!showEmailContactModal)

  const getCompanyDeals = () => {
    if (!companyDeals.length) {
      dispatch({
        type: ACT.ACTIVE_COMPANY_DEALS_FETCH_REQUESTED,
        payload: {
          companyId: contact.company.id
        }
      })
    }
  }

  const removeContactFromDeal = async (dealId, dealName) => {
    const willRemove = await swal({
      title: `Are you sure you want to remove ${contact.firstName} from opportunity ${dealName}?`,
      text: '',
      buttons: ['No', 'Yes'],
      icon: 'warning'
    })
    if (willRemove) {
      dispatch({
        type: ACT.REMOVE_DEAL_CONTACT_REQUESTED,
        payload: {
          dealId,
          contactId: contact.id
        }
      })
    }
  }

  return (
    <Container>
      <section className={s.dealsContainer}>
        <div className={s.buttons}>
          <div className={s.topButtons}>
            <Button
              onClick={() => {
                toggleEmailContactModal()
                setEmailTemplateType(EMAIL_TEMPLATE_TYPE.COMPANY_OPEN_OPPORTUNITIES)
              }}
            >
              <FaEnvelope />
              Email {contact.firstName} about all open Opportunities with {contact.company.name}
            </Button>
            <Button
              onClick={() => {
                toggleEmailContactModal()
                setEmailTemplateType(EMAIL_TEMPLATE_TYPE.CONTACT_OPEN_OPPORTUNITIES)
              }}
            >
              <FaEnvelope />
              Email {contact.firstName} about all open Opportunities
            </Button>
          </div>
          <Tabs
            activeTabKey="active"
            className={s.main}
            tabs={[
              {
                key: 'active',
                label: 'Active/Open',
                cb: () => filterDeals(CONTACT_DEALS_STATUS.ACTIVE),
                value: (
                  <DealsTable
                    contactDeals={contactDeals}
                    removeContactFromDeal={removeContactFromDeal}
                    label="Active/Open Opportunities"
                  />
                )
              },
              {
                key: 'won',
                label: 'Won',
                cb: () => filterDeals(CONTACT_DEALS_STATUS.WON),
                value: (
                  <DealsTable
                    contactDeals={contactDeals}
                    removeContactFromDeal={removeContactFromDeal}
                    label="Won Opportunities"
                  />
                )
              },
              {
                key: 'lost',
                label: 'Lost',
                cb: () => filterDeals(CONTACT_DEALS_STATUS.LOST),
                value: (
                  <DealsTable
                    contactDeals={contactDeals}
                    removeContactFromDeal={removeContactFromDeal}
                    label="Lost Opportunities"
                  />
                )
              },
              {
                key: 'project',
                label: 'Project',
                cb: () => filterDeals(CONTACT_DEALS_STATUS.PROJECT),
                value: (
                  <DealsTable
                    contactDeals={contactDeals}
                    removeContactFromDeal={removeContactFromDeal}
                    label="Project Opportunities"
                  />
                )
              },
              {
                key: 'closed',
                label: 'Cancelled (not active)',
                cb: () => filterDeals(CONTACT_DEALS_STATUS.CLOSED),
                value: (
                  <DealsTable
                    contactDeals={contactDeals}
                    removeContactFromDeal={removeContactFromDeal}
                    label="Cancelled (not active) Opportunities"
                  />
                )
              },
              {
                key: 'all',
                label: 'All Active for the Company',
                cb: () => getCompanyDeals(),
                value: (
                  <DealsTable
                    companyDeals={companyDeals}
                    label="All Active Opportunities for the Company"
                  />
                )
              }
            ]}
          />
        </div>
      </section>
      <EmailContactModalContainer
        toggleEmailContactModal={toggleEmailContactModal}
        showEmailContactModal={showEmailContactModal}
        emailTemplateType={emailTemplateType}
        contact={contact}
      />
    </Container>
  )
}

export default Deals
